import type { StringKey } from '@ipfabric/utils';
import colors from 'containers/App/colors';
import type { FormikProps } from 'formik';
import type { JSX } from 'react';
import Select, {
  type SingleValue,
  type ActionMeta,
  type Props as ReactSelectProps,
} from 'react-select';
import { fieldHasError } from 'utils/formik/fieldHasError';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

type OptionType = {
  readonly value: string;
  readonly label: string;
};

type Props<TFormikValues, TOptionType extends OptionType> = Omit<
  ReactSelectProps<TOptionType, false>,
  'options' | 'isMulti'
> & {
  options: TOptionType[];
  formik: FormikProps<TFormikValues>;
  id?: string;
  name: StringKey<TFormikValues> | NestedFormikName;
  onValueChange?: (value: TOptionType['value']) => void;
} & Record<string, unknown>;

const borderColorValue = '#d2d6de';
const focusedBorderColorValue = '#3c8dbc !important';

export const FormikSelectFieldLegacy = <
  TFormikValues,
  TOptionType extends OptionType,
>(
  props: Props<TFormikValues, TOptionType>,
): JSX.Element => {
  const field = props.formik.getFieldProps(props.name);
  const hasError = fieldHasError<TFormikValues>(props.formik, props.name);

  const handleChange = (
    option: SingleValue<TOptionType>,
    actionMeta: ActionMeta<TOptionType>,
  ) => {
    if (option) {
      field.onChange({
        target: {
          name: field.name,
          value: option.value,
        },
      });
      props.onValueChange?.(option.value);
      props.onChange?.(option, actionMeta);
    }
  };
  const handleBlur = () =>
    field.onBlur({
      target: {
        name: field.name,
      },
    });

  return (
    <Select
      {...props}
      classNamePrefix='react-select'
      name={field.name}
      onBlur={props.onBlur || handleBlur}
      onChange={handleChange}
      styles={{
        control: (styles, { isFocused }) => ({
          ...styles,
          borderColor: isFocused ? focusedBorderColorValue : borderColorValue,
          '&:hover': { borderColor: borderColorValue },
        }),
        ...props.styles,
        ...(hasError && {
          control: (base) => ({ ...base, borderColor: colors.danger }),
        }),
      }}
      theme={(theme) => ({
        ...theme,
        borderRadius: 0,
        spacing: {
          ...theme.spacing,
          baseUnit: 2,
          controlHeight: 34,
        },
      })}
      value={
        props.options?.find(
          (option): boolean => option.value === field.value,
        ) ?? null
      }
    />
  );
};
